import { sanitizeHtml } from '../../utils/safeHtml';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import radiologyResultService from '../../services/radiologyResultService';
import './RadiologyTemplatePickerModal.css';

/**
 * RadiologyTemplatePickerModal
 * High-performance searchable template selection modal.
 * Supports live search across thousands of templates, investigation filtering,
 * doctor filtering, favorite templates (⭐), template previewing, and cloning into
 * transaction without touching master templates.
 */
export default function RadiologyTemplatePickerModal({
  isOpen,
  onClose,
  investigationId,
  investigationName,
  doctorName = '',
  doctorId = null,
  initialTemplates = [],
  onSelectTemplate,
  onStartBlank
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDoctorOnly, setFilterDoctorOnly] = useState(false);
  const [templates, setTemplates] = useState(initialTemplates || []);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [viewTab, setViewTab] = useState('ALL'); // 'ALL' | 'FAVORITES'
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [selectedTag, setSelectedTag] = useState('ALL');

  const TAGS = ['ALL', 'Normal', 'Trauma', 'Emergency', 'Follow-up', 'Post-op', 'Contrast', 'Screening', 'Pediatric'];

  // Load favorites from localStorage
  useEffect(() => {
    try {
      const key = `rad_favorites_${doctorId || 'default'}`;
      const saved = JSON.parse(localStorage.getItem(key) || '[]');
      setFavoriteIds(saved);
    } catch (e) {
      console.warn('Could not read favorites from localStorage:', e);
    }
  }, [doctorId, isOpen]);

  // Toggle favorite
  const toggleFavorite = (tmplId, e) => {
    if (e) e.stopPropagation();
    try {
      const key = `rad_favorites_${doctorId || 'default'}`;
      let updated;
      if (favoriteIds.includes(tmplId)) {
        updated = favoriteIds.filter(id => id !== tmplId);
      } else {
        updated = [...favoriteIds, tmplId];
      }
      setFavoriteIds(updated);
      localStorage.setItem(key, JSON.stringify(updated));
    } catch (err) {
      console.warn('Could not toggle favorite:', err);
    }
  };

  // Debounced server search for scalable large template libraries
  const loadTemplates = useCallback(async (searchQuery = '') => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await radiologyResultService.searchTemplates({
        investigationId: investigationId || undefined,
        searchTerm: searchQuery || undefined,
        pageSize: 50,
        pageNumber: 1
      });

      if (res.success && res.data) {
        const items = res.data.items || [];
        setTemplates(items);
        if (items.length > 0) {
          setSelectedTemplate(items[0]);
        } else {
          setSelectedTemplate(null);
        }
      }
    } catch (err) {
      console.error('Failed to search templates:', err);
      // Fall back to initial templates if available
      if (initialTemplates && initialTemplates.length > 0) {
        setTemplates(initialTemplates);
        setSelectedTemplate(initialTemplates[0]);
      } else {
        setErrorMsg('Failed to load templates. You can still start with a blank report.');
      }
    } finally {
      setLoading(false);
    }
  }, [investigationId, initialTemplates]);

  // Load when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setFilterDoctorOnly(false);
      setViewTab('ALL');
      loadTemplates('');
    }
  }, [isOpen, loadTemplates]);

  // Handle Search Input Change
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
  };

  // Trigger search on Enter or debounced
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      loadTemplates(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, isOpen, loadTemplates]);

  // Client-side filter by doctor, favorites tab, and smart tag
  const filteredTemplates = useMemo(() => {
    let list = templates;
    if (filterDoctorOnly && doctorName) {
      const docLower = doctorName.toLowerCase();
      list = list.filter(t => t.doctorName && t.doctorName.toLowerCase().includes(docLower));
    }
    if (viewTab === 'FAVORITES') {
      list = list.filter(t => favoriteIds.includes(t.templateId));
    }
    if (selectedTag && selectedTag !== 'ALL') {
      list = list.filter(t => t.tags && t.tags.toLowerCase().includes(selectedTag.toLowerCase()));
    }
    return list;
  }, [templates, filterDoctorOnly, doctorName, viewTab, favoriteIds, selectedTag]);

  // Use selected template
  const handleConfirmTemplate = () => {
    if (!selectedTemplate) return;
    onSelectTemplate(selectedTemplate);
    onClose();
  };

  // Start Blank Report
  const handleBlankReport = () => {
    if (onStartBlank) onStartBlank();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="rad-modal-overlay">
      <div className="rad-picker-modal">
        {/* Header */}
        <div className="rad-picker-header">
          <div>
            <h2>
              <span>📑</span> Select Radiology Report Template
            </h2>
            <p>
              Modality Investigation: <strong>{investigationName || 'Imaging Test'}</strong>
            </p>
          </div>
          <button className="rad-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Search & Filter Bar */}
        <div className="rad-picker-search-bar">
          <div className="rad-picker-input-wrap">
            <span className="rad-picker-icon">🔍</span>
            <input
              type="text"
              className="rad-picker-input"
              placeholder="Search templates (e.g. Normal, Fracture, Trauma, Stroke, Infiltrate)..."
              value={searchTerm}
              onChange={handleSearchChange}
              autoFocus
            />
            {searchTerm && (
              <button
                className="rad-picker-clear"
                onClick={() => setSearchTerm('')}
              >
                ✕
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              type="button"
              className={`rad-tab-btn ${viewTab === 'ALL' ? 'active' : ''}`}
              onClick={() => setViewTab('ALL')}
            >
              All ({templates.length})
            </button>
            <button
              type="button"
              className={`rad-tab-btn ${viewTab === 'FAVORITES' ? 'active' : ''}`}
              onClick={() => setViewTab('FAVORITES')}
            >
              ⭐ Favorites ({favoriteIds.length})
            </button>
          </div>

          {doctorName && (
            <label className="rad-picker-filter-doc">
              <input
                type="checkbox"
                checked={filterDoctorOnly}
                onChange={e => setFilterDoctorOnly(e.target.checked)}
              />
              <span>Only Dr. {doctorName}</span>
            </label>
          )}
        </div>

        {/* Smart Template Tags Bar */}
        <div className="rad-picker-tags-bar" style={{ display: 'flex', gap: '6px', overflowX: 'auto', padding: '6px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', alignSelf: 'center', marginRight: '4px' }}>🏷️ TAGS:</span>
          {TAGS.map(tag => (
            <button
              key={tag}
              type="button"
              className={`rad-tag-chip ${selectedTag === tag ? 'active' : ''}`}
              style={{
                fontSize: '11px',
                padding: '3px 10px',
                borderRadius: '12px',
                border: '1px solid',
                borderColor: selectedTag === tag ? '#2563eb' : '#cbd5e1',
                background: selectedTag === tag ? '#eff6ff' : '#ffffff',
                color: selectedTag === tag ? '#1d4ed8' : '#475569',
                cursor: 'pointer',
                fontWeight: selectedTag === tag ? 600 : 500,
                transition: 'all 0.15s ease'
              }}
              onClick={() => setSelectedTag(tag)}
            >
              {tag}
            </button>
          ))}
        </div>

        {errorMsg && (
          <div className="rad-picker-error">
            {errorMsg}
          </div>
        )}

        {/* Content Pane: Left List + Right Preview */}
        <div className="rad-picker-body">
          {/* Left: Template List */}
          <div className="rad-picker-list-col">
            <div className="rad-picker-list-header">
              <span>Matching Templates ({filteredTemplates.length})</span>
              {loading && <span className="rad-picker-loading">Searching...</span>}
            </div>

            <div className="rad-picker-list">
              {filteredTemplates.length === 0 ? (
                <div className="rad-picker-empty">
                  <p>No matching templates found.</p>
                  <button className="rad-btn-blank-inline" onClick={handleBlankReport}>
                    Start Blank Report Instead
                  </button>
                </div>
              ) : (
                filteredTemplates.map(t => {
                  const isSelected = selectedTemplate?.templateId === t.templateId;
                  const isFav = favoriteIds.includes(t.templateId);
                  return (
                    <div
                      key={t.templateId}
                      className={`rad-picker-item ${isSelected ? 'active' : ''}`}
                      onClick={() => setSelectedTemplate(t)}
                    >
                      <div className="rad-picker-item-top">
                        <span className="rad-picker-item-title">
                          <button
                            type="button"
                            onClick={(e) => toggleFavorite(t.templateId, e)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', fontSize: '13px' }}
                            title={isFav ? 'Remove from favorites' : 'Mark as favorite'}
                          >
                            {isFav ? '⭐' : '☆'}
                          </button>
                          {t.templateTitle || t.title}
                        </span>
                        {t.isDefault && <span className="rad-badge-default">Default</span>}
                      </div>
                      <div className="rad-picker-item-sub">
                        <span>{t.doctorName ? `Dr. ${t.doctorName}` : 'Standard / Global'}</span>
                        {t.modality && <span className="rad-badge-modality">{t.modality}</span>}
                      </div>
                      {t.tags && (
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
                          {t.tags.split(',').map((tg, idx) => (
                            <span key={idx} style={{ fontSize: '10px', background: '#e0f2fe', color: '#0369a1', padding: '1px 6px', borderRadius: '4px', fontWeight: 500 }}>
                              #{tg.trim()}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right: Template Preview Canvas */}
          <div className="rad-picker-preview-col">
            <div className="rad-picker-preview-header">
              <span>Template Content Snapshot Preview</span>
              {selectedTemplate && (
                <span className="rad-picker-preview-badge">
                  ID: #{selectedTemplate.templateId}
                </span>
              )}
            </div>

            <div className="rad-picker-preview-content">
              {selectedTemplate ? (
                <div className="rad-picker-preview-doc">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <h3 style={{ margin: 0 }}>{selectedTemplate.templateTitle || selectedTemplate.title}</h3>
                    <button
                      type="button"
                      className="rad-quick-tmpl-pill fav"
                      onClick={(e) => toggleFavorite(selectedTemplate.templateId, e)}
                    >
                      {favoriteIds.includes(selectedTemplate.templateId) ? '⭐ In Favorites' : '☆ Add to Favorites'}
                    </button>
                  </div>
                  <div
                    className="rad-picker-preview-html"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(selectedTemplate.templateDesc || '<p><em>(Empty template content)</em></p>') }}
                  />
                </div>
              ) : (
                <div className="rad-picker-no-selection">
                  <p>Select a template on the left to preview its clinical findings and impression.</p>
                </div>
              )}
            </div>

            <div className="rad-picker-immutability-note">
              🔒 <strong>Master Template Safety:</strong> Selecting a template loads an independent copy into this patient's transaction. The master template remains 100% immutable and unaffected by any doctor edits.
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="rad-picker-footer">
          <div className="rad-picker-footer-left">
            <button
              type="button"
              className="rad-btn-blank"
              onClick={handleBlankReport}
            >
              📄 Start Blank Report
            </button>
          </div>

          <div className="rad-picker-footer-right">
            <button
              type="button"
              className="rad-btn-cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rad-btn-use-template"
              disabled={!selectedTemplate}
              onClick={handleConfirmTemplate}
            >
              ✔ Use Selected Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
